/**
 * The shelves: what the collection is physically stored in.
 *
 * Two datatables, mirroring the Cards screen: the containers table (searched,
 * filtered by kind, sorted by header) and, behind each container's name, a
 * modal with that container's own bins table. A bin's name opens a details
 * modal where its rename, export and delete live - the tables themselves
 * carry no action buttons, only checkboxes and names. Adding a container, and
 * adding a bin, each live in their own modal behind a link.
 *
 * The order on screen defaults to the walk order, because that is the order a
 * person walks and the order a picklist is built in. A bin's `slotHighWater`
 * is shown beside its count on purpose: it is the number the scanner's next
 * slot comes from, so when the two disagree (a card was removed by hand) the
 * screen says so instead of hiding it.
 */

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, type Api } from '../api/http.js';
import type { Bin, Container, Meta } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { ExportButton } from '../components/ExportButton.js';
import type { Async } from '../state/useAsync.js';
import { loadSettings } from '../settings.js';
import { binsInWalkOrder, containerOrder } from '../storage/walk.js';

export interface StorageScreenProps {
  api: Api;
  meta: Meta | null;
  /** Owned by the shell, so the Scan tab sees a bin the moment it is created. */
  containers: Async<Container[]>;
  reloadContainers: () => void;
}

type ContainerSortKey = 'name' | 'kind' | 'bins' | 'cards' | 'walk';

const CONTAINER_COLUMNS: ReadonlyArray<{ key: ContainerSortKey; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'kind', label: 'Kind' },
  { key: 'bins', label: 'Bins' },
  { key: 'cards', label: 'Cards' },
  { key: 'walk', label: 'Walk' },
];

type BinSortKey = 'label' | 'cards' | 'slot' | 'walk';

const BIN_COLUMNS: ReadonlyArray<{ key: BinSortKey; label: string }> = [
  { key: 'label', label: 'Bin' },
  { key: 'cards', label: 'Cards' },
  { key: 'slot', label: 'Next slot' },
  { key: 'walk', label: 'Walk' },
];

const cardsIn = (container: Container): number => container.bins.reduce((total, bin) => total + bin.copyCount, 0);

function containerCell(container: Container, key: ContainerSortKey): string | number {
  switch (key) {
    case 'name':
      return container.label;
    case 'kind':
      return container.kind;
    case 'bins':
      return container.bins.length;
    case 'cards':
      return cardsIn(container);
    case 'walk':
      return container.sortOrder;
  }
}

function binCell(bin: Bin, key: BinSortKey): string | number {
  switch (key) {
    case 'label':
      return bin.label;
    case 'cards':
      return bin.copyCount;
    case 'slot':
      return bin.slotHighWater;
    case 'walk':
      return bin.sortOrder;
  }
}

const compareText = (left: string | number, right: string | number): number =>
  typeof left === 'number' || typeof right === 'number'
    ? Number(left) - Number(right)
    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });

export function StorageScreen({ api, meta, containers, reloadContainers: reload }: StorageScreenProps) {
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [sort, setSort] = useState<{ key: ContainerSortKey; descending: boolean }>({ key: 'walk', descending: false });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [openPublicId, setOpenPublicId] = useState<string | null>(null);

  const kinds = meta?.vocabularies.containerKinds ?? ['box', 'binder', 'drawer', 'case'];

  const list = containers.status === 'ready' ? containerOrder(containers.value) : [];
  const openContainer = openPublicId === null ? null : (list.find((item) => item.publicId === openPublicId) ?? null);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = list.filter((container) => {
      if (needle !== '' && !`${container.label} ${container.kind}`.toLowerCase().includes(needle)) return false;
      if (kindFilter !== '' && container.kind !== kindFilter) return false;
      return true;
    });
    filtered.sort((left, right) => {
      const order = compareText(containerCell(left, sort.key), containerCell(right, sort.key));
      // Walk order is the only one with a meaningful tiebreak: the label.
      const tie = sort.key === 'walk' ? left.label.localeCompare(right.label) : 0;
      return (order === 0 ? tie : order) * (sort.descending ? -1 : 1);
    });
    return filtered;
  }, [list, kindFilter, query, sort]);

  const filtersActive = query.trim() !== '' || kindFilter !== '';
  const resetFilters = (): void => {
    setQuery('');
    setKindFilter('');
  };

  const toggleSort = (key: ContainerSortKey): void =>
    setSort((current) => (current.key === key ? { key, descending: !current.descending } : { key, descending: false }));

  const toggleOne = (publicId: string): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });

  const toggleAllVisible = (): void =>
    setSelected((current) => {
      const visible = rows.map((row) => row.publicId);
      const allSelected = visible.length > 0 && visible.every((id) => current.has(id));
      if (allSelected) {
        const next = new Set(current);
        for (const id of visible) next.delete(id);
        return next;
      }
      return new Set([...current, ...visible]);
    });

  // Deleting is two presses: the first names what would go, the second does it.
  // A container that still holds cards is refused by the API with the reason on
  // screen, so the confirm never has to guess whether it is safe.
  const remove = async (): Promise<void> => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    let failures = 0;
    let detail: string | null = null;
    for (const publicId of selected) {
      try {
        await api.deleteContainer(publicId);
      } catch (failure) {
        failures += 1;
        // The first refusal names the reason (a bin that still holds cards);
        // a count alone would not say what to move.
        if (detail === null) detail = failure instanceof ApiError ? failure.message : 'it could not be deleted';
      }
    }
    setSelected(new Set());
    setConfirmingDelete(false);
    setBusy(false);
    if (failures > 0) setError(detail ?? `${failures} container${failures === 1 ? '' : 's'} could not be deleted.`);
    reload();
  };

  return (
    <div className="stack">
      <header className="row session-head">
        <div>
          <h2>Bins</h2>
          <p className="hint">
            Containers and their bins, in the order you walk past them. A card is filed into whichever bin is open when
            it is scanned, so the labels are how you find it again.
          </p>
          <p className="hint">CSV uses ManaBox headers, one row per card, with the shelf address kept.</p>
        </div>
        <ExportButton api={api} target={{ scope: 'collection', label: 'whole collection' }} />
      </header>

      {error && <Banner kind="error">{error}</Banner>}
      {containers.status === 'loading' && (
        <>
          <p role="status" aria-live="polite">
            Loading your storage…
          </p>
          <div className="card" aria-busy="true" aria-hidden="true">
            <div className="skeleton" />
          </div>
        </>
      )}
      {containers.status === 'error' && (
        <Banner kind="error" onRetry={reload}>
          {containers.message}
        </Banner>
      )}

      {containers.status === 'ready' && (
        <div className="toolbar">
          <div className="row session-head">
            <p className="hint tnum" role="status" aria-live="polite">
              {rows.length === list.length
                ? `${list.length} container${list.length === 1 ? '' : 's'}`
                : `${rows.length} of ${list.length} containers`}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </p>
            <button type="button" className="linklike" onClick={() => setShowAdd(true)}>
              Add a container
            </button>
            {selected.size > 0 &&
              (confirmingDelete ? (
                <span className="export">
                  <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
                    {busy ? 'Deleting…' : `Confirm delete ${selected.size} container${selected.size === 1 ? '' : 's'}`}
                  </button>
                  <button type="button" className="ghost" onClick={() => setConfirmingDelete(false)}>
                    Keep them
                  </button>
                </span>
              ) : (
                <button type="button" className="danger" onClick={() => setConfirmingDelete(true)}>
                  Delete {selected.size} container{selected.size === 1 ? '' : 's'}
                </button>
              ))}
          </div>

          {list.length === 0 ? (
            <div className="card">
              <h3>Nothing to file into yet</h3>
              <p className="hint">
                A container is a box, a binder, a drawer or a case. Bins live inside it, and a bin is what a scan lands
                in - one row of a box, one page of a binder.
              </p>
            </div>
          ) : (
            <>
              <div className="row">
                <label className="grow">
                  Search containers
                  <input
                    type="search"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Label or kind…"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    maxLength={120}
                  />
                </label>
              </div>
              <div className="row filters">
                <label>
                  Kind
                  <select
                    value={kindFilter}
                    onChange={(event) => setKindFilter(event.target.value)}
                    aria-label="Filter by kind"
                  >
                    <option value="">All kinds</option>
                    {kinds.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                {filtersActive && (
                  <button type="button" className="ghost" onClick={resetFilters}>
                    Reset
                  </button>
                )}
              </div>

              {rows.length === 0 ? (
                <div className="card">
                  <h3>No containers match these filters.</h3>
                  <p className="hint">Clear the search to see the whole walk again.</p>
                  <button type="button" className="ghost" onClick={resetFilters}>
                    Reset filters
                  </button>
                </div>
              ) : (
                <div className="table-scroll" role="region" aria-label="Containers table" tabIndex={0}>
                  <table className="collection">
                    <thead>
                      <tr>
                        <th scope="col" className="cell-select">
                          <input
                            type="checkbox"
                            aria-label="Select all visible containers"
                            checked={rows.length > 0 && rows.every((row) => selected.has(row.publicId))}
                            onChange={toggleAllVisible}
                          />
                        </th>
                        {CONTAINER_COLUMNS.map((column) => (
                          <th
                            key={column.key}
                            aria-sort={sort.key === column.key ? (sort.descending ? 'descending' : 'ascending') : 'none'}
                            scope="col"
                          >
                            <button type="button" className="sort" onClick={() => toggleSort(column.key)}>
                              <span>Sort by {column.label.toLowerCase()}</span>
                              <span aria-hidden="true">
                                {sort.key === column.key ? (sort.descending ? ' ▼' : ' ▲') : ''}
                              </span>
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((container) => (
                        <tr key={container.publicId} className={selected.has(container.publicId) ? 'selected' : undefined}>
                          <td className="cell-select">
                            <input
                              type="checkbox"
                              aria-label={`Select ${container.label}`}
                              checked={selected.has(container.publicId)}
                              onChange={() => toggleOne(container.publicId)}
                            />
                          </td>
                          <th scope="row">
                            <button type="button" className="name-btn" onClick={() => setOpenPublicId(container.publicId)}>
                              {container.label}
                            </button>
                          </th>
                          <td data-label="Kind">
                            <span className="chip">{container.kind}</span>
                          </td>
                          <td className="tnum" data-label="Bins">{container.bins.length}</td>
                          <td className="tnum" data-label="Cards">{cardsIn(container)}</td>
                          <td className="tnum" data-label="Walk">#{container.sortOrder + 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showAdd &&
        createPortal(
          <AddContainerModal
            api={api}
            kinds={kinds}
            nextSortOrder={
              containers.status === 'ready' && containers.value.length > 0
                ? Math.max(...containers.value.map((item) => item.sortOrder)) + 1
                : 0
            }
            onClose={() => setShowAdd(false)}
            onAdded={() => {
              setShowAdd(false);
              reload();
            }}
          />,
          document.body,
        )}

      {openContainer &&
        createPortal(
          <ContainerModal
            api={api}
            kinds={kinds}
            container={openContainer}
            onClose={() => setOpenPublicId(null)}
            onChanged={reload}
          />,
          document.body,
        )}
    </div>
  );
}

/**
 * A new container, behind the link above the table.
 *
 * A container goes at the end of the walk, which is what its position means:
 * the API stores `sortOrder` and orders by it, so the default has to be the
 * next free position rather than zero, or every container would claim the same one.
 */
function AddContainerModal({
  api,
  kinds,
  nextSortOrder,
  onClose,
  onAdded,
}: {
  api: Api;
  kinds: string[];
  nextSortOrder: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [label, setLabel] = useState('');
  // The Settings screen's default prefills this, without owning it: a stored
  // value the server no longer offers falls back to the first kind.
  const [kindOverride, setKindOverride] = useState<string | null>(null);
  const storedKind = loadSettings().containerKind;
  const kind = kindOverride ?? (storedKind !== null && kinds.includes(storedKind) ? storedKind : (kinds[0] ?? 'box'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createContainer({ label, kind, sortOrder: nextSortOrder });
      onAdded();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the container could not be created');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell label="Add a container" onClose={onClose}>
      <p className="hint">A container sits on the shelf; bins live inside it. It joins the end of the walk.</p>
      <form onSubmit={(event) => void add(event)}>
        <label>
          Container label
          <input
            name="label"
            autoComplete="off"
            spellCheck={false}
            placeholder="BOX A…"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            required
            maxLength={64}
          />
        </label>
        <label>
          Kind
          <select name="kind" value={kind} onChange={(event) => setKindOverride(event.target.value)}>
            {kinds.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        {error && <Banner kind="error">{error}</Banner>}
        <div className="row">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add container'}
          </button>
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/**
 * One container, up close: its label and kind at the top, its bins in a
 * datatable below, and a link to add the next bin.
 *
 * The bins table mirrors the containers table - search, sort headers,
 * checkboxes - and a bin's name opens its details, where renaming, exporting
 * and deleting live. Deleting is two presses, and a bin that still holds cards
 * is refused by the API with the reason on screen.
 */
function ContainerModal({
  api,
  kinds,
  container,
  onClose,
  onChanged,
}: {
  api: Api;
  kinds: string[];
  container: Container;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [binQuery, setBinQuery] = useState('');
  const [binSort, setBinSort] = useState<{ key: BinSortKey; descending: boolean }>({ key: 'walk', descending: false });
  const [binSelected, setBinSelected] = useState<ReadonlySet<string>>(new Set());
  const [binConfirmingDelete, setBinConfirmingDelete] = useState(false);
  const [label, setLabel] = useState(container.label);
  const [kind, setKind] = useState(container.kind);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddBin, setShowAddBin] = useState(false);
  const [openBinPublicId, setOpenBinPublicId] = useState<string | null>(null);

  const dirty = label !== container.label || kind !== container.kind;
  const openBin = openBinPublicId === null ? null : (container.bins.find((bin) => bin.publicId === openBinPublicId) ?? null);

  const saveContainer = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateContainer(container.publicId, { label, kind });
      onChanged();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'it could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const binRows = useMemo(() => {
    const needle = binQuery.trim().toLowerCase();
    const filtered = binsInWalkOrder(container).filter((bin) => needle === '' || bin.label.toLowerCase().includes(needle));
    filtered.sort((left, right) => {
      const order = compareText(binCell(left, binSort.key), binCell(right, binSort.key));
      const tie = binSort.key === 'walk' ? left.label.localeCompare(right.label) : 0;
      return (order === 0 ? tie : order) * (binSort.descending ? -1 : 1);
    });
    return filtered;
  }, [container, binQuery, binSort]);

  const toggleBinSort = (key: BinSortKey): void =>
    setBinSort((current) =>
      current.key === key ? { key, descending: !current.descending } : { key, descending: false },
    );

  const toggleOneBin = (publicId: string): void =>
    setBinSelected((current) => {
      const next = new Set(current);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });

  const toggleAllBins = (): void =>
    setBinSelected((current) => {
      const visible = binRows.map((row) => row.publicId);
      const allSelected = visible.length > 0 && visible.every((id) => current.has(id));
      if (allSelected) {
        const next = new Set(current);
        for (const id of visible) next.delete(id);
        return next;
      }
      return new Set([...current, ...visible]);
    });

  const removeBins = async (): Promise<void> => {
    if (binSelected.size === 0) return;
    setBusy(true);
    setError(null);
    let failures = 0;
    let detail: string | null = null;
    for (const publicId of binSelected) {
      try {
        await api.deleteBin(container.publicId, publicId);
      } catch (failure) {
        failures += 1;
        if (detail === null) detail = failure instanceof ApiError ? failure.message : 'it could not be deleted';
      }
    }
    setBinSelected(new Set());
    setBinConfirmingDelete(false);
    setBusy(false);
    if (failures > 0) setError(detail ?? `${failures} bin${failures === 1 ? '' : 's'} could not be deleted.`);
    onChanged();
  };

  return (
    <ModalShell label={`${container.label} bins`} wide onClose={onClose}>
      <div className="container-title">
        <h3>
          <span className="bin-name">{container.label}</span> <span className="chip">{container.kind}</span>
        </h3>
        <span className="hint tnum">walk #{container.sortOrder + 1}</span>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      <form className="row" onSubmit={(event) => void saveContainer(event)}>
        <label className="grow">
          Container label
          <input
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            required
          />
        </label>
        <label>
          Kind
          <select value={kind} onChange={(event) => setKind(event.target.value)}>
            {kinds.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary" disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      <div className="toolbar">
        <div className="row session-head">
          <p className="hint tnum" role="status" aria-live="polite">
            {binRows.length === container.bins.length
              ? `${container.bins.length} bin${container.bins.length === 1 ? '' : 's'}`
              : `${binRows.length} of ${container.bins.length} bins`}
            {binSelected.size > 0 && ` · ${binSelected.size} selected`}
          </p>
          <button type="button" className="linklike" onClick={() => setShowAddBin(true)}>
            Add a bin
          </button>
          {binSelected.size > 0 &&
            (binConfirmingDelete ? (
              <span className="export">
                <button type="button" className="danger" disabled={busy} onClick={() => void removeBins()}>
                  {busy ? 'Deleting…' : `Confirm delete ${binSelected.size} bin${binSelected.size === 1 ? '' : 's'}`}
                </button>
                <button type="button" className="ghost" onClick={() => setBinConfirmingDelete(false)}>
                  Keep them
                </button>
              </span>
            ) : (
              <button type="button" className="danger" onClick={() => setBinConfirmingDelete(true)}>
                Delete {binSelected.size} bin{binSelected.size === 1 ? '' : 's'}
              </button>
            ))}
        </div>

        <div className="row">
          <label className="grow">
            Search bins
            <input
              type="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Bin label…"
              value={binQuery}
              onChange={(event) => setBinQuery(event.target.value)}
              maxLength={120}
            />
          </label>
        </div>
      </div>

      {container.bins.length === 0 ? (
        <p className="hint">No bins yet: add one before scanning into this container.</p>
      ) : binRows.length === 0 ? (
        <p className="hint">No bins match this search.</p>
      ) : (
        <div className="table-scroll" role="region" aria-label={`${container.label} bins table`} tabIndex={0}>
          <table className="collection">
            <thead>                <tr>
                  <th scope="col" className="cell-select">
                    <input
                      type="checkbox"
                      aria-label="Select all visible bins"
                    checked={binRows.length > 0 && binRows.every((row) => binSelected.has(row.publicId))}
                    onChange={toggleAllBins}
                  />
                </th>
                {BIN_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    aria-sort={binSort.key === column.key ? (binSort.descending ? 'descending' : 'ascending') : 'none'}
                    scope="col"
                  >
                    <button type="button" className="sort" onClick={() => toggleBinSort(column.key)}>
                      <span>Sort by {column.label.toLowerCase()}</span>
                      <span aria-hidden="true">
                        {binSort.key === column.key ? (binSort.descending ? ' ▼' : ' ▲') : ''}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {binRows.map((bin) => (
                <tr key={bin.publicId} className={binSelected.has(bin.publicId) ? 'selected' : undefined}>
                  <td className="cell-select">
                    <input
                      type="checkbox"
                      aria-label={`Select ${bin.label}`}
                      checked={binSelected.has(bin.publicId)}
                      onChange={() => toggleOneBin(bin.publicId)}
                    />
                  </td>
                  <th scope="row">
                    <button type="button" className="name-btn" onClick={() => setOpenBinPublicId(bin.publicId)}>
                      {bin.label}
                    </button>
                  </th>                    <td className="tnum" data-label="Cards">
                      {bin.copyCount} card{bin.copyCount === 1 ? '' : 's'}
                    </td>
                    <td className="tnum" data-label="Next slot">#{bin.slotHighWater + 1}</td>
                    <td className="tnum" data-label="Walk">#{bin.sortOrder + 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddBin && (
        <AddBinModal
          api={api}
          container={container}
          onClose={() => setShowAddBin(false)}
          onAdded={() => {
            setShowAddBin(false);
            onChanged();
          }}
        />
      )}

      {openBin && (
        <BinModal
          api={api}
          containerPublicId={container.publicId}
          bin={openBin}
          onClose={() => setOpenBinPublicId(null)}
          onChanged={onChanged}
          onDeleted={() => {
            setOpenBinPublicId(null);
            onChanged();
          }}
        />
      )}
    </ModalShell>
  );
}

/**
 * A new bin, behind the link in the container's modal.
 *
 * A bin goes at the end of its container's walk, so the default position is
 * the next free one rather than zero.
 */
function AddBinModal({
  api,
  container,
  onClose,
  onAdded,
}: {
  api: Api;
  container: Container;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createBin(container.publicId, {
        label,
        sortOrder:
          container.bins.length === 0 ? 0 : Math.max(...container.bins.map((bin) => bin.sortOrder)) + 1,
      });
      onAdded();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the bin could not be created');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell label={`Add a bin to ${container.label}`} onClose={onClose}>
      <form onSubmit={(event) => void add(event)}>
        <label>
          Bin label
          <input
            aria-label={`New bin in ${container.label}`}
            placeholder="DRAWER 2…"
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            required
          />
        </label>
        {error && <Banner kind="error">{error}</Banner>}
        <div className="row">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add bin'}
          </button>
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/**
 * One bin, up close: what it holds, its export, and the two things a person
 * may do to it - rename it, or delete it when it is empty.
 */
function BinModal({
  api,
  containerPublicId,
  bin,
  onClose,
  onChanged,
  onDeleted,
}: {
  api: Api;
  containerPublicId: string;
  bin: Bin;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [draft, setDraft] = useState(bin.label);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = draft !== bin.label;

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateBin(containerPublicId, bin.publicId, { label: draft });
      onChanged();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'it could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteBin(containerPublicId, bin.publicId);
      onDeleted();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'it could not be deleted');
      setConfirmingDelete(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell label={`${bin.label} details`} onClose={onClose}>
      <div className="container-title">
        <h3>
          <span className="bin-name">{bin.label}</span>
        </h3>
        <span className="hint tnum">
          {bin.copyCount} card{bin.copyCount === 1 ? '' : 's'} · next slot #{bin.slotHighWater + 1}
        </span>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      <form className="row" onSubmit={(event) => void save(event)}>
        <label className="grow">
          Bin name
          <input
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            required
          />
        </label>
        <button type="submit" className="primary" disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>

      <div className="row">
        <ExportButton api={api} target={{ scope: 'bin', id: bin.publicId, label: bin.label }} />
        {confirmingDelete ? (
          <span className="export">
            <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : `Confirm delete ${bin.label}`}
            </button>
            <button type="button" className="ghost" onClick={() => setConfirmingDelete(false)}>
              Keep {bin.label}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="ghost danger-text"
            aria-label={`Delete bin ${bin.label}`}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </button>
        )}
      </div>
    </ModalShell>
  );
}

/**
 * The sheet every Bins modal shares: a backdrop, Escape and backdrop-click to
 * leave, and focus into the dialog - the same contract as the Cards modals.
 */
function ModalShell({
  label,
  wide,
  onClose,
  children,
}: {
  label: string;
  wide?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={wide === true ? 'modal wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={(element) => element?.focus()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="modal-head">
          <div />
          <button type="button" className="ghost" onClick={onClose} aria-label="Close dialog">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
